import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useOSStore } from '../stores/osStore';
import type { FaseOS } from '../types';
import FaseCard from '../components/FaseCard';
import LoadingScreen from '../components/LoadingScreen';
import { useTrackingStore } from '../stores/trackingStore';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    activeTargets,
    nativeTargets,
    isTracking,
    sincronizarComFasesEmRota,
    stopTracking,
  } = useTrackingStore();
  const {
    ordensServico,
    ordemAtiva,
    setOrdemAtiva,
    fetchOrdensAtribuidas,
    isLoading,
    error,
    clearError,
  } = useOSStore();
  const [ordensCarregadas, setOrdensCarregadas] = useState(false);

  useEffect(() => {
    fetchOrdensAtribuidas().then(() => {
      if (!useOSStore.getState().error) setOrdensCarregadas(true);
    });
  }, [fetchOrdensAtribuidas]);

  // Retoma o rastreamento das fases "Em Rota" (ex.: página recarregada) e encerra as que saíram de rota
  useEffect(() => {
    if (!ordensCarregadas || !user) return;
    const emRota = ordensServico.flatMap((os) =>
      os.fases
        .filter(
          (fase) =>
            fase.id_motorista_atribuido === user.id && fase.status_fase === 'Em Rota'
        )
        .map((fase) => ({ osId: os.id, faseId: fase.id }))
    );
    sincronizarComFasesEmRota(emRota);
  }, [ordensCarregadas, ordensServico, user, sincronizarComFasesEmRota]);

  const getFasesAtribuidas = (): FaseOS[] => {
    if (!ordemAtiva || !user) return [];

    const atribuidas = ordemAtiva.fases
      .filter(
        (fase) =>
          fase.id_motorista_atribuido === user.id &&
          fase.status_fase !== 'Concluída'
      )
      .sort((a, b) => a.ordem - b.ordem);

    if (ordemAtiva.velorio_sepultamento_mesmo_local) {
      const sepultamento = ordemAtiva.fases.find((f) => f.nome_fase === 'Sepultamento');
      const temVelorioAtivo = atribuidas.some((f) => f.nome_fase === 'Velório');
      if (
        sepultamento &&
        temVelorioAtivo &&
        !atribuidas.some((f) => f.nome_fase === 'Sepultamento')
      ) {
        return [...atribuidas, sepultamento].sort((a, b) => a.ordem - b.ordem);
      }
    }

    return atribuidas;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Pendente':
        return 'text-yellow-600 bg-yellow-50';
      case 'Em Andamento':
        return 'text-blue-600 bg-blue-50';
      case 'Concluída':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleLogout = () => {
    stopTracking();
    logout();
    navigate('/login', { replace: true });
  };

  if (isLoading) {
    return <LoadingScreen message="Carregando suas fases..." />;
  }

  const rastreando = isTracking || nativeTargets.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow border-b border-gray-300 p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Nexus Motorista</h1>
          <p className="text-xs text-gray-500">Gerenciamento de Fases</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{user?.nome}</p>
            <p className="text-xs text-gray-500">Motorista</p>
          </div>
          <button type="button" className="btn-secondary" onClick={handleLogout}>
            Sair
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 max-w-md w-full mx-auto">
        <div className="mb-4 card p-4">
          <div className="flex justify-between items-center mb-3">
            <div>
              <p className="text-xs text-gray-500">Agora</p>
              <p className="text-lg font-semibold text-gray-900">
                {rastreando ? 'Rastreamento em andamento' : 'Pronto para iniciar'}
              </p>
            </div>
            <span className={`chip ${rastreando ? 'chip-green' : 'chip-gray'}`}>
              {rastreando
                ? `📍 ${activeTargets.length + nativeTargets.length} ativo(s)`
                : '⏸️ inativo'}
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn-secondary btn-icon flex-1"
              onClick={() => navigate('/status')}
            >
              📡 Status
            </button>
            <button
              type="button"
              className="btn-primary btn-icon flex-1"
              onClick={() => fetchOrdensAtribuidas()}
            >
              🔄 Atualizar
            </button>
          </div>
          {isTracking && activeTargets.length > 0 && (
            <div className="mt-2 text-xs text-gray-600">
              Enviando a mesma posição para {activeTargets.length} OS/Fase. Mantenha esta tela
              aberta para não interromper o envio.
            </div>
          )}
          {nativeTargets.length > 0 && (
            <div className="mt-2 text-xs text-gray-600">
              {nativeTargets.length} fase(s) rastreada(s) pelo app nativo.
            </div>
          )}
        </div>

        {error && (
          <div className="error-box mb-4 flex justify-between items-center">
            <span>{error}</span>
            <button
              type="button"
              className="text-sm underline"
              onClick={clearError}
            >
              Fechar
            </button>
          </div>
        )}

        {ordensServico.length > 0 && (
          <div className="mb-4 card p-4">
            <label
              htmlFor="os-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Selecione uma OS
            </label>
            <select
              id="os-select"
              value={ordemAtiva?.id ?? ''}
              onChange={(e) => {
                const id = parseInt(e.target.value, 10);
                const selected = ordensServico.find((os) => os.id === id);
                setOrdemAtiva(selected ?? null);
              }}
            >
              {ordensServico.map((os) => (
                <option key={os.id} value={os.id}>
                  {os.numero_nota_contratacao
                    ? `N.C. ${os.numero_nota_contratacao}`
                    : `OS #${os.codigo_acesso}`}{' '}
                  – {os.nome_falecido}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              As fases seguem a ordem da OS. Finalize a anterior antes de iniciar a próxima.
            </p>
          </div>
        )}

        {ordemAtiva && (
          <div className="mb-6 card p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {ordemAtiva.numero_nota_contratacao
                  ? `N.C. ${ordemAtiva.numero_nota_contratacao}`
                  : `Ordem de Serviço #${ordemAtiva.codigo_acesso}`}
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  ordemAtiva.status_geral
                )}`}
              >
                {ordemAtiva.status_geral}
              </span>
            </div>
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <p className="font-medium text-gray-900">Falecido</p>
                <p className="text-gray-500">{ordemAtiva.nome_falecido}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Criada em</p>
                <p className="text-gray-500">
                  {new Date(ordemAtiva.criado_em).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Fases Concluídas</p>
                <p className="text-gray-500">
                  {ordemAtiva.fases.filter((f) => f.status_fase === 'Concluída')
                    .length}{' '}
                  / {ordemAtiva.fases.length}
                </p>
              </div>
            </div>
          </div>
        )}

        {ordemAtiva && getFasesAtribuidas().length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Suas fases (toque para iniciar / finalizar)
            </h3>
            {getFasesAtribuidas().map((fase) => (
              <FaseCard
                key={fase.id}
                fase={fase}
                ordemServico={ordemAtiva}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-2xl mb-4">📋</p>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma fase atribuída
            </h3>
            <p className="text-gray-500 text-sm">
              {ordemAtiva
                ? 'Você não tem fases atribuídas nesta OS.'
                : 'Selecione uma ordem de serviço para ver suas fases.'}
            </p>
          </div>
        )}

        {ordensServico.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-2xl mb-4">🚗</p>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma ordem de serviço atribuída
            </h3>
            <p className="text-gray-500 text-sm">
              Aguarde o administrador atribuir fases a você.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
